import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { envelopeApi, gatewaysApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Mono, CodePill } from "@/components/Atoms";
import { Pager, usePager } from "@/components/Pager";
import CodeEditor from "@/components/CodeEditor";
import {
  Plus, Trash2, Pencil, CheckCircle2, XCircle, Search, Code as CodeIcon,
  Info as InfoIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useCan } from "@/lib/rbac";

const TABS = [
  { k: "routes", label: "Routes" },
  { k: "dsl", label: "DSL parsers" },
  { k: "scripts", label: "Starlark scripts" },
  { k: "test", label: "Test parser" },
];

export default function EnvelopeRules() {
  const [tab, setTab] = useState("routes");
  return (
    <PageContainer>
      <PageHeader title="Gateway envelope rules" subtitle="Routes, DSL parsers and Starlark scripts that transform raw frames into telemetry." />
      <div className="flex gap-1 border-b border-zinc-200 mb-4" role="tablist">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`env-tab-${t.k}`} role="tab" aria-selected={tab === t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === t.k ? "border-[#3730A3] text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "routes" && <Routes />}
      {tab === "dsl" && <DSL />}
      {tab === "scripts" && <Scripts />}
      {tab === "test" && <TestParser />}
    </PageContainer>
  );
}

function RouteFormFields({ form, setForm, hwList, parsers }) {
  return (
    <>
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Canonical HW</span>
        <select
          data-testid="route-input-canonical_hw"
          value={form.canonical_hw}
          onChange={(e) => setForm({ ...form, canonical_hw: e.target.value })}
          className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">Select hardware…</option>
          {hwList.map((h) => <option key={h.id} value={h.canonical_hw}>{h.canonical_hw} · {h.name}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Flag</span>
        <input
          data-testid="route-input-flag"
          className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono"
          placeholder="0x01"
          value={form.flag}
          onChange={(e) => setForm({ ...form, flag: e.target.value })}
        />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Parser</span>
        <select
          data-testid="route-input-parser_name"
          value={form.parser_name}
          onChange={(e) => setForm({ ...form, parser_name: e.target.value })}
          className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">Select parser…</option>
          {parsers.map((p) => <option key={p.name} value={p.name}>{p.name} ({p.kind || "script"})</option>)}
        </select>
      </label>
    </>
  );
}

function Routes() {
  const qc = useQueryClient();
  const canWrite = useCan("write:envelope");
  const list = useQuery({ queryKey: ["env-routes"], queryFn: envelopeApi.routes });
  const hw = useQuery({ queryKey: ["gw-hw"], queryFn: gatewaysApi.hwList });
  const dsl = useQuery({ queryKey: ["env-dsl"], queryFn: envelopeApi.dslList });
  const scripts = useQuery({ queryKey: ["env-scripts"], queryFn: envelopeApi.scriptsList });

  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState("");
  const [hwFilter, setHwFilter] = useState("");
  const [parserFilter, setParserFilter] = useState("");

  const inval = () => qc.invalidateQueries({ queryKey: ["env-routes"] });
  const cM = useMutation({ mutationFn: envelopeApi.routeCreate, onSuccess: () => { toast.success("Route added"); inval(); setModal(null); } });
  const uM = useMutation({ mutationFn: ({ id, data }) => envelopeApi.routeUpdate(id, data), onSuccess: () => { toast.success("Route updated"); inval(); setModal(null); } });
  const dM = useMutation({ mutationFn: envelopeApi.routeRemove, onSuccess: () => { toast.success("Route deleted"); inval(); } });

  const allParsers = [
    ...(dsl.data || []).map((d) => ({ name: d.name, kind: d.kind })),
    ...(scripts.data || []).map((s) => ({ name: s.name, kind: "starlark" })),
  ];

  const hwByCanonical = Object.fromEntries(
    (hw.data || []).map((h) => [h.canonical_hw, h])
  );

  const filtered = (list.data || []).filter((r) => {
    if (search) {
      const s = search.toLowerCase();
      const hwMatch = hwByCanonical[r.canonical_hw];
      if (!(
        (r.canonical_hw || "").toLowerCase().includes(s) ||
        (r.parser_name || "").toLowerCase().includes(s) ||
        (hwMatch?.name || "").toLowerCase().includes(s) ||
        (hwMatch?.vendor || "").toLowerCase().includes(s)
      )) return false;
    }
    if (hwFilter && r.canonical_hw !== hwFilter) return false;
    if (parserFilter && r.parser_name !== parserFilter) return false;
    return true;
  });
  const pager = usePager(filtered, { defaultSize: 50, sizes: [25, 50, 100] });

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-200 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-2">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
          <input
            data-testid="route-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search model, canonical HW, parser…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-200 rounded-md focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
          />
        </div>
        <select
          data-testid="route-filter-hw"
          value={hwFilter}
          onChange={(e) => setHwFilter(e.target.value)}
          className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All hardware</option>
          {hw.data?.map((h) => <option key={h.id} value={h.canonical_hw}>{h.canonical_hw}</option>)}
        </select>
        <select
          data-testid="route-filter-parser"
          value={parserFilter}
          onChange={(e) => setParserFilter(e.target.value)}
          className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All parsers</option>
          {allParsers.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
        </select>
      </div>

      <div className="px-4 py-2.5 border-b border-zinc-200 flex justify-between items-center gap-3">
        <div className="text-xs text-zinc-500 font-mono">{filtered.length} of {list.data?.length || 0} routes</div>
        {canWrite && (
          <button data-testid="route-new" onClick={() => setModal({ mode: "create", row: { canonical_hw: "", flag: "0x01", parser_name: "" } })} className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900 inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New route</button>
        )}
      </div>

      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="text-left p-3 font-semibold">Gateway model</th>
            <th className="text-left p-3 font-semibold">Canonical HW</th>
            <th className="text-left p-3 font-semibold">Flag</th>
            <th className="text-left p-3 font-semibold">→ Parser</th>
            <th className="w-24"></th>
          </tr>
        </thead>
        <tbody>
          {pager.pageRows.map((r) => {
            const hwRow = hwByCanonical[r.canonical_hw];
            return (
              <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`route-row-${r.id}`}>
                <td className="p-3">
                  {hwRow ? (
                    <>
                      <div className="font-medium">{hwRow.name}</div>
                      <div className="text-xs text-zinc-500 font-mono">{hwRow.vendor} · {hwRow.protocol}</div>
                    </>
                  ) : (
                    <span className="text-xs text-zinc-400 italic">unknown HW</span>
                  )}
                </td>
                <td className="p-3"><Mono>{r.canonical_hw}</Mono></td>
                <td className="p-3"><CodePill>{r.flag}</CodePill></td>
                <td className="p-3"><Mono>{r.parser_name}</Mono></td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    {canWrite && (
                      <>
                        <button
                          data-testid={`route-edit-${r.id}`}
                          onClick={() => setModal({ mode: "edit", row: { ...r } })}
                          className="p-1.5 rounded hover:bg-zinc-100"
                          title="Edit"
                        ><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`route-delete-${r.id}`} onClick={() => { if (window.confirm("Delete route?")) dM.mutate(r.id); }} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {pager.total === 0 && (
            <tr><td colSpan={5} className="py-10 text-center text-sm text-zinc-500">No routes match the filters.</td></tr>
          )}
        </tbody>
      </table>
      <Pager {...pager} testidPrefix="route-pager" />

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-bold mb-3">{modal.mode === "create" ? "New route" : "Edit route"}</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (!modal.row.canonical_hw || !modal.row.flag || !modal.row.parser_name) {
                return toast.error("All fields are required");
              }
              if (modal.mode === "create") cM.mutate(modal.row);
              else uM.mutate({ id: modal.row.id, data: modal.row });
            }} className="space-y-3">
              <RouteFormFields
                form={modal.row}
                setForm={(r) => setModal({ ...modal, row: r })}
                hwList={hw.data || []}
                parsers={allParsers}
              />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModal(null)} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm">Cancel</button>
                <button data-testid="route-save" type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Try to pretty-print JSON. If invalid, return the original string untouched.
function tryBeautifyJson(text) {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

function DSL() {
  const qc = useQueryClient();
  const canWrite = useCan("write:envelope");
  const list = useQuery({ queryKey: ["env-dsl"], queryFn: envelopeApi.dslList });
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [kind, setKind] = useState("json");
  const [name, setName] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (selected) {
      // Auto-beautify JSON when loading an existing parser.
      const raw = selected.spec_json || "";
      setDraft(selected.kind === "json" ? tryBeautifyJson(raw) : raw);
      setKind(selected.kind);
      setName(selected.name);
    }
  }, [selected]);

  const save = useMutation({
    mutationFn: () => envelopeApi.dslSave(name, { kind, spec_json: draft }),
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["env-dsl"] }); },
  });

  const validate = async () => {
    const r = await envelopeApi.dslValidate(draft);
    setResult(r);
    if (r.ok) toast.success(r.message); else toast.error(r.message);
  };

  const format = () => {
    if (kind !== "json") return toast.message("Format only available for JSON parsers");
    try {
      const pretty = JSON.stringify(JSON.parse(draft), null, 2);
      setDraft(pretty);
      toast.success("Formatted");
    } catch (e) {
      toast.error("Invalid JSON: " + e.message);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">parsers</span>
          <button data-testid="dsl-new" onClick={() => { setSelected(null); setName(""); setKind("json"); setDraft("{\n  \n}"); }} className="text-zinc-600 hover:text-zinc-900"><Plus className="w-4 h-4" /></button>
        </div>
        <ul>
          {list.data?.map((d) => (
            <li key={d.name}>
              <button onClick={() => setSelected(d)} className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 ${selected?.name === d.name ? "bg-zinc-50" : ""}`}>
                <Mono>{d.name}</Mono>
                <span className="ml-2 text-[10px] uppercase text-zinc-500 font-mono">{d.kind}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <input data-testid="dsl-name" placeholder="parser_name" value={name} onChange={(e) => setName(e.target.value)} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm font-mono flex-1" />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm bg-white">
            <option value="json">json</option>
            <option value="tlv">tlv</option>
          </select>
          <button
            data-testid="dsl-format"
            onClick={format}
            className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50 inline-flex items-center gap-1.5"
            title="Pretty-print JSON"
          ><CodeIcon className="w-3.5 h-3.5" /> Format</button>
          <button data-testid="dsl-validate" onClick={validate} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50">Validate</button>
          {canWrite && (
            <button data-testid="dsl-save" onClick={() => name && save.mutate()} className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm">Save</button>
          )}
        </div>
        <CodeEditor value={draft} onChange={setDraft} language="json" height="380px" />
        {result && (
          <div className={`mt-3 text-sm flex items-center gap-2 ${result.ok ? "text-lime-600" : "text-red-600"}`}>
            {result.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {result.message}
          </div>
        )}
      </div>
    </div>
  );
}

function Scripts() {
  const qc = useQueryClient();
  const canWrite = useCan("write:envelope");
  const list = useQuery({ queryKey: ["env-scripts"], queryFn: envelopeApi.scriptsList });
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [name, setName] = useState("");
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if (selected) { setDraft(selected.code); setName(selected.name); }
  }, [selected]);

  const save = useMutation({
    mutationFn: () => envelopeApi.scriptSave(name, { code: draft }),
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["env-scripts"] }); },
  });

  const validate = async () => {
    const r = await envelopeApi.scriptValidate(draft);
    setResult(r);
    if (r.ok) toast.success(r.message); else toast.error(r.message);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">scripts</span>
          <button data-testid="script-new" onClick={() => { setSelected(null); setName(""); setDraft("def parse(envelope):\n    return {}\n"); }} className="text-zinc-600 hover:text-zinc-900"><Plus className="w-4 h-4" /></button>
        </div>
        <ul>
          {list.data?.map((s) => (
            <li key={s.name}>
              <button onClick={() => setSelected(s)} className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 ${selected?.name === s.name ? "bg-zinc-50" : ""}`}>
                <Mono>{s.name}</Mono>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-3 bg-white border border-zinc-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <input data-testid="script-name" placeholder="script_name" value={name} onChange={(e) => setName(e.target.value)} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm font-mono flex-1" />
          <button data-testid="script-validate" onClick={validate} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50">Validate</button>
          {canWrite && (
            <button data-testid="script-save" onClick={() => name && save.mutate()} className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm">Save</button>
          )}
        </div>
        <CodeEditor value={draft} onChange={setDraft} language="python" height="380px" />
        {result && (
          <div className={`mt-3 text-sm flex items-center gap-2 ${result.ok ? "text-lime-600" : "text-red-600"}`}>
            {result.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {result.message}
          </div>
        )}
      </div>
    </div>
  );
}

function TestParser() {
  const dsl = useQuery({ queryKey: ["env-dsl"], queryFn: envelopeApi.dslList });
  const scripts = useQuery({ queryKey: ["env-scripts"], queryFn: envelopeApi.scriptsList });
  const hw = useQuery({ queryKey: ["gw-hw"], queryFn: gatewaysApi.hwList });

  const allParsers = [
    ...(dsl.data || []).map((d) => ({ name: d.name, kind: d.kind })),
    ...(scripts.data || []).map((s) => ({ name: s.name, kind: "starlark" })),
  ];

  const [payload, setPayload] = useState("AABBCCDDEEFF0011223344");
  const [parser, setParser] = useState("");
  const [flag, setFlag] = useState("0x01");
  const [gwHw, setGwHw] = useState("");
  const [out, setOut] = useState(null);
  const [running, setRunning] = useState(false);

  // Auto-select first parser/hw once loaded.
  useEffect(() => { if (!parser && allParsers[0]) setParser(allParsers[0].name); }, [allParsers, parser]);
  useEffect(() => { if (!gwHw && hw.data?.[0]) setGwHw(hw.data[0].canonical_hw); }, [hw.data, gwHw]);

  const run = async () => {
    if (!parser) return toast.error("Pick a parser first");
    setRunning(true);
    setOut(null);
    try {
      const r = await envelopeApi.testParser({
        parser_name: parser,
        payload_hex: payload,
        flag,
        gw_hw: gwHw,
      });
      setOut(JSON.stringify(r, null, 2));
      if (r.ok) toast.success("Parser ran successfully");
      else toast.error(r.error || "Parser returned an error");
    } catch (e) {
      setOut(JSON.stringify({ ok: false, error: e.message }, null, 2));
      toast.error(e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Explanation block */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm" data-testid="parser-test-explanation">
        <div className="flex items-start gap-2.5">
          <InfoIcon className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
          <div className="text-blue-900 space-y-2">
            <div className="font-semibold">What this sandbox does</div>
            <p>
              You provide a <strong>hex payload</strong>, the <strong>flag</strong> the gateway would
              have stamped on the frame, the <strong>gateway hardware</strong> it would have come from,
              and the <strong>parser</strong> you want to test.
            </p>
            <p>
              The backend (<Mono className="text-xs">POST /api/envelope/test</Mono>) runs the selected
              parser against your payload <em>without</em> persisting any telemetry, then returns the
              decoded envelope it would have produced. Use this to sanity-check a parser before publishing
              it as part of an envelope route — or to debug why a real device&apos;s frames aren&apos;t decoding.
            </p>
            <p className="text-xs text-blue-700">
              No gateways or devices need to be online. No data is written to the database. Telemetry
              counters and dashboards are unaffected.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Parser</span>
              <select
                data-testid="parser-test-parser"
                value={parser}
                onChange={(e) => setParser(e.target.value)}
                className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="">— pick parser —</option>
                {allParsers.map((p) => <option key={p.name} value={p.name}>{p.name} ({p.kind || "script"})</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Gateway HW</span>
              <select
                data-testid="parser-test-gw_hw"
                value={gwHw}
                onChange={(e) => setGwHw(e.target.value)}
                className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
              >
                {hw.data?.map((h) => <option key={h.id} value={h.canonical_hw}>{h.canonical_hw}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Flag</span>
            <input
              data-testid="parser-test-flag"
              className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono"
              value={flag}
              onChange={(e) => setFlag(e.target.value)}
              placeholder="0x01"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Payload (hex)</span>
            <textarea
              data-testid="parser-test-payload"
              rows={6}
              className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-xs font-mono"
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
            />
          </label>
          <button
            data-testid="parser-test-run"
            onClick={run}
            disabled={running}
            className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900 disabled:opacity-50"
          >{running ? "Running…" : "Run parser"}</button>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-2">Output</div>
          <CodeEditor value={out || "// run parser to see output"} onChange={() => {}} language="json" height="380px" readOnly />
        </div>
      </div>
    </div>
  );
}
